<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quiz.title }}</h2>
      <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
      <div class="question-box">
        <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
        <ul class="options-list">
          <li
            v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
            :key="index"
            @click="selectOption(index)"
            :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
            class="option"
          >
            {{ option }}
            <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
        <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
          </li>
        </ul>
      </div>
      <div v-if="isAnswerRevealed" class="feedback">
        <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
          {{ feedbackMessage }}
        </p>
        
      </div>
      <div class="navigation-buttons">
        <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
        <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
      </div>
      <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
        <h4>Quiz Summary</h4>
        <p>Correct answers: {{ correctAnswersCount }}</p>
        <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
        <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
        <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
      </div>
    </div><br><br><br><br><br><br>
  </template>
  
  <script>
import { ref } from 'vue';

export default {
  setup() {
  
    const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/nyse.m4a');
    const quiz = ref({
      title: "Understanding Credit Cards",
      questions: [
        {
          question: "What is a credit card?",
          options: [
            "A card that allows you to spend money up to a pre-approved limit",
            "A card that directly withdraws money from your checking account",
            "A prepaid card that you can load with money",
            "A loyalty card that earns you points at specific stores"
          ],
          correctAnswer: 0
        },
        {
          question: "What does APR stand for?",
          options: [
            "Annual Percentage Rate",
            "Automated Payment Requirement",
            "Adjusted Payment Rate",
            "Annual Payment Ratio"
          ],
          correctAnswer: 0
        },
        {
          question: "What can affect your credit card's APR?",
          options: [
            "Your payment history and credit score",
            "The type of purchases you make",
            "The amount of money you withdraw at an ATM",
            "The number of credit cards you own"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a credit score?",
          options: [
            "A numerical expression based on a level analysis of a person's credit files",
            "A score given by credit card companies to reward loyal customers",
            "A federal rating that determines your tax level",
            "A score that determines your eligibility for student loans"
          ],
          correctAnswer: 0
        },
        {
          question: "Why is it important to pay at least the minimum payment on your credit card on time?",
          options: [
            "To avoid late fees and negative impacts on your credit score",
            "To earn reward points or cashback",
            "To increase your credit card limit",
            "To decrease your APR"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a balance transfer?",
          options: [
            "Withdrawing cash from your credit card",
            "Transferring your savings to your credit card account",
            "Moving the balance from one credit card to another, often to save on interest rates",
            "Correcting errors in your credit card statement"
          ],
          correctAnswer: 2
        },
        {
          question: "What does it mean to 'max out' your credit card?",
          options: [
            "To pay off your credit card in full",
            "To reach the maximum reward points available",
            "To use up all of your available credit limit",
            "To have the card for the maximum amount of time allowed"
          ],
          correctAnswer: 2
        },
        {
          question: "What is a cash advance?",
          options: [
            "Transferring money from your credit card to your checking account",
            "Withdrawing cash from your credit card at an ATM",
            "Getting cash back at a store checkout",
            "Receiving an advance on your paycheck through your credit card"
          ],
          correctAnswer: 1
        },
        {
          question: "How can you avoid paying interest on purchases made with your credit card?",
          options: [
            "By only using your card for large purchases",
            "By paying the full balance on your card before the due date each month",
            "By transferring the balance to another card",
            "By withdrawing cash instead of making purchases"
          ],
          correctAnswer: 1
        },
        {
          question: "What is a secured credit card?",
          options: [
            "A card secured against theft or loss",
            "A card that requires a security deposit as collateral",
            "A card with enhanced security features for online shopping",
            "A card that guarantees secure investments"
          ],
          correctAnswer: 1
        }
      ]
    });

    const currentQuestionIndex = ref(0);
    const correctAnswersCount = ref(0);
    const incorrectAnswersCount = ref(0);
    const selectedOptionIndex = ref(-1);
    const isAnswerRevealed = ref(false);
    const isCorrect = ref(false);
    const feedbackMessage = ref("");

    const selectOption = (index) => {
      if (isAnswerRevealed.value) return;
      selectedOptionIndex.value = index;
      isAnswerRevealed.value = true;

      if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
        isCorrect.value = true;
        correctAnswersCount.value++;
        feedbackMessage.value = "Correct!";
        correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
      } else {
        isCorrect.value = false;
        incorrectAnswersCount.value++;
        feedbackMessage.value = "Incorrect.";
      }
    };

    const nextQuestion = () => {
      if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
        currentQuestionIndex.value++;
        resetState();
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--;
        resetState();
      }
    };

    const retakeQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswersCount.value = 0;
      incorrectAnswersCount.value = 0;
      resetState();
    };

    const resetState = () => {
      selectedOptionIndex.value = -1;
      isAnswerRevealed.value = false;
      isCorrect.value = false;
      feedbackMessage.value = "";
    };

    return {
      quiz,
      currentQuestionIndex,
      correctAnswersCount,
      incorrectAnswersCount,
      selectedOptionIndex,
      isAnswerRevealed,
      isCorrect,
      feedbackMessage,
      selectOption,
      nextQuestion,
      previousQuestion,
      retakeQuiz,
    };
  },
};
</script>



<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>